import type {
  AccessControlChangeReviewStateParams,
  AccessControlDirectory,
  AccessControlDirectoryGroupsAndUsersParams,
  AccessControlDirectoryGroups,
  AccessControlDirectoryParams,
  AccessControlDirectoryUsers,
  AccessControlFile,
  AccessControlFolder,
  AccessControlFoldersParams,
  AccessControlPeerFolders,
  FetchPeerFoldersParams,
  DepartmentFilterParams,
  DepartmentFilter
} from './accessFoldersSlice'
import {
  AccessFoldersReviewStatuses,
  AccessFoldersTypes,
  UserAccessEmployeeType,
  FolderAccessModes,
  AccessFolderTabs
} from './pages/folders/types'
import { getAfterCursor } from '../../utils/graphqlUtil'
import { LIMIT_DEFAULT } from '../../constants'
import { gql } from 'graphql-request'

export const getAccessFolderFilters = (params: AccessControlFoldersParams) => {
  const {
    page = 1,
    datasourceId,
    hasOpenObjects,
    hasSensitiveObjects,
    parentId,
    search = '',
    accessTypes,
    reviewStatuses,
    ownerIds,
    isInherited,
    countType,
    sensitivity,
    attributes
  } = params
  const cursor = getAfterCursor(page, LIMIT_DEFAULT)
  const filterPagination = `first: ${LIMIT_DEFAULT}, after: "${cursor}",`

  let filterStr = ``
  if (datasourceId) {
    filterStr += `datasourceId: "${datasourceId}",`
  }
  if (countType) {
    filterStr += `countType: ${countType},`
  }
  if (hasOpenObjects !== undefined) {
    filterStr += `hasOpenObjects: ${hasOpenObjects},`
  }
  if (hasSensitiveObjects !== undefined) {
    filterStr += `hasSensitiveObjects: ${hasSensitiveObjects},`
  }

  if (parentId !== undefined) {
    filterStr += `parentId: ${parentId},`
  }

  if (search) {
    filterStr += `search: "${search}",`
  }
  if (accessTypes?.length) {
    filterStr += `accessTypes: [${accessTypes}]`
  }
  if (reviewStatuses?.length) {
    filterStr += `reviewStatuses: [${reviewStatuses}]`
  }
  if (ownerIds?.length) {
    filterStr += `ownerIds: [${ownerIds}]`
  }
  if (isInherited !== undefined) {
    filterStr += `isInherited: ${isInherited}`
  }
  if (sensitivity?.length) {
    filterStr += `sensitivity: [${sensitivity}],`
  }
  if (attributes?.length) {
    filterStr += `attributeTypeIds: ${JSON.stringify(attributes)},`
  }

  return `${filterPagination} ${filterStr}`
}

export const queryAccessFolders = (params: AccessControlFoldersParams) => {
  const filterString = getAccessFolderFilters(params)
  return gql`
    {
      accessControlDirectory(${filterString}) {
        count
        edges {
          node {
            id
            name
            accessTypes
            reviewStatus
            path
            inherited
            objectLink
            lastModifiedTime
            groupAccess { count }
            userAccess { count }
            owner { name, id }
            accessControlDirectoryCounts {
              files {
                total
                needsReview
                sensitive
                inheritingPermissions
                specificPermissions
              }
              unfilteredFiles{
                total
                sensitive
              }
              directFiles {
                total
                needsReview
                sensitive
                inheritingPermissions
                specificPermissions
              }
              directFolders {
                total
                needsReview
                sensitive
                inheritingPermissions
                specificPermissions
              }
              folders {
                total
                needsReview
                sensitive
                inheritingPermissions
                specificPermissions
              }
              attributeCounts {
                count
                attribute {
                  edges {
                    node {
                      id
                      name
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  `
}

export const mutationChangeAccessFoldersReviewState = (
  params: AccessControlChangeReviewStateParams
): string => {
  return gql`
    mutation {
      updateAccessControlDirectoryReviewStatus(
        clientMutationId: "1"
        datasourceId: "${params.datasourceId}"
        ids: [${params.ids}]
        reviewStatus: ${params.reviewStatus}
        accessTypeCategory: ${params.accessType}
      ) {
        clientMutationId
        reviewStatus
        ids
      }
    }
  `
}

export const mapQueryAccessFolders = (raw: any): { list: AccessControlFolder[]; total: number } => {
  try {
    const list =
      raw?.accessControlDirectory?.edges?.map(({ node }) => ({
        id: node?.id,
        name: node?.name,
        accessTypes: node?.accessTypes,
        groupAccessCount: node?.groupAccess?.count || 0,
        userAccessCount: node?.userAccess?.count || 0,
        owner: node?.owner,
        reviewStatus: node?.reviewStatus,
        path: node?.path,
        inherited: node?.inherited,
        objectLink: node?.objectLink,
        lastModifiedTime: node?.lastModifiedTime,
        files: node?.accessControlDirectoryCounts?.files,
        unfilteredFiles: node?.accessControlDirectoryCounts?.unfilteredFiles,
        directFiles: node?.accessControlDirectoryCounts?.directFiles,
        directFolders: node?.accessControlDirectoryCounts?.directFolders,
        folders: node?.accessControlDirectoryCounts?.folders,
        attributeFindings: node?.accessControlDirectoryCounts?.attributeCounts?.map((attr) => ({
          id: attr?.attribute?.edges?.[0]?.node?.id,
          name: attr?.attribute?.edges?.[0]?.node?.name,
          filesCount: attr?.count
        }))
      })) || []

    return { list, total: raw?.accessControlDirectory?.count || 0 }
  } catch (error) {
    console.error(error)
    throw error
  }
}

const accessFoldersTypesFilter: Record<AccessFoldersTypes, string> = {
  [AccessFoldersTypes.Open]: 'hasOpenAccess: true',
  [AccessFoldersTypes.Restricted]: 'hasOpenAccess: false',
  [AccessFoldersTypes.External]: 'hasExternalAccess: true',
  [AccessFoldersTypes.Internal]: 'hasExternalAccess: false',
  [AccessFoldersTypes.Excessive]: 'hasExcessiveAccess: true',
  [AccessFoldersTypes.Appropriate]: 'hasExcessiveAccess: false',
  [AccessFoldersTypes.Intact]: 'isInherited: true',
  [AccessFoldersTypes.Broken]: 'isInherited: false'
} as const

export const queryAccessFiles = (params: AccessControlFoldersParams) => {
  const {
    datasourceId,
    parentId,
    reviewStatuses,
    hasOpenObjects = false,
    isInherited,
    directoryAccessTypeCategory,
    accessTypes,
    sensitivity,
    attributes,
    page
  } = params

  const cursor = getAfterCursor(page, LIMIT_DEFAULT)
  const filterPagination = `first: ${LIMIT_DEFAULT}, after: "${cursor}",`

  const accessType = accessTypes && accessTypes.length > 0 ? accessTypes[0] : undefined

  let filterStr = ''
  if (hasOpenObjects) {
    filterStr += `hasOpenAccess: ${hasOpenObjects},`
  }
  if (isInherited !== undefined) {
    filterStr += `isInherited: ${isInherited},`
  }
  if (sensitivity?.length) {
    filterStr += `sensitivity: [${sensitivity}],`
  }
  if (attributes?.length) {
    filterStr += `attributeTypeIds: ${JSON.stringify(attributes)},`
  }

  return gql`
    {
      accessControlObjects(
        datasourceIds: "${datasourceId}"
        ${parentId !== undefined ? `parentId: ${parentId}` : ''}
        ${reviewStatuses?.length ? `reviewStatuses: [${reviewStatuses}]` : ''}
        ${accessType ? accessFoldersTypesFilter[accessType] : ''}
        ${filterStr}
        ${
          directoryAccessTypeCategory
            ? `directoryAccessTypeCategory: ${directoryAccessTypeCategory}`
            : ''
        }
        ${filterPagination}
      ) {
        count
        edges {
          node {
            id
            name
            object {
              edges {
                node {
                  id
                  labels {
                    edges {
                      node {
                        id
                        name
                      }
                    }
                  }
                  attribute {
                    edges {
                      node {
                        id
                        name
                      }
                    }
                  }
                }
              }
            }
            isOpen
            hasExternalAccess
            hasExcessiveAccess
            isInherited
            sensitivityLevel
            reviewStatus
            objectLink
            lastModifiedTime
            userAccess {
              count
            }
            groupAccess {
              count
            }
            owner {
              edges {
                node {
                  email
                }
              }
            }
          }
        }
      }
    }
  `
}

function extractAccessFileAccesType(
  node: any,
  directoryAccessTypeCategory: AccessFolderTabs | undefined
) {
  switch (directoryAccessTypeCategory) {
    case AccessFolderTabs.Open:
      return [node.isOpen ? AccessFoldersTypes.Open : AccessFoldersTypes.Restricted]
    case AccessFolderTabs.External:
      return [node.hasExternalAccess ? AccessFoldersTypes.External : AccessFoldersTypes.Internal]
    case AccessFolderTabs.Excessive:
      return [
        node.hasExcessiveAccess ? AccessFoldersTypes.Excessive : AccessFoldersTypes.Appropriate
      ]
    case AccessFolderTabs.InheritanceIssues:
      return [node.isInherited ? AccessFoldersTypes.Intact : AccessFoldersTypes.Broken]
    default:
      return []
  }
}

export const mapQueryAccessFiles = (
  raw: any,
  directoryAccessTypeCategory: AccessFolderTabs | undefined
) => {
  const files = raw.accessControlObjects?.edges?.map(({ node }) => {
    const file: AccessControlFile = {
      id: node.object.edges[0]?.node.id || node.id,
      fileId: node.id,
      name: node.name,
      sensitivityLevel: node.sensitivityLevel,
      reviewStatus: node.reviewStatus,
      accessTypes: extractAccessFileAccesType(node, directoryAccessTypeCategory),
      accessibleUsersCount: node.userAccess.count,
      accessibleGroupsCount: node.groupAccess.count,
      objectLink: node.objectLink,
      labels: node.object.edges[0]?.node.labels.edges.map((edge) => edge.node),
      attributes: node.object.edges[0]?.node.attribute.edges.map((edge) => edge.node),
      lastModifiedTime: node.lastModifiedTime,
      ...(node.owner?.edges[0]?.node
        ? {
            owner: {
              email: node.owner?.edges[0]?.node.email
            }
          }
        : {})
    }

    return file
  })

  return { list: files ?? [], count: raw.accessControlObjects?.count ?? 0 }
}

export const queryAccessControlDirectory = (params: AccessControlDirectoryParams) => {
  const { folderId, searchQuery, datasourceId, countType } = params

  const filterStr = ''

  return gql`
    {
      accessControlDirectory(
        datasourceId: "${datasourceId}"
        id: ${folderId}
        countType: ${countType}
        ${filterStr}
        ${searchQuery ? `search: "${searchQuery}"` : ''}
      ) {
        edges {
          node {
            id
            name
            path
            objectLink
            inherited
            groupAccess {
              count
            }
            userAccess {
              count
            }
            owner {
              name
              id
            }
            lastModifiedTime
            accessTypes
            accessControlDirectoryCounts {
              files {
                total
                needsReview
                sensitive
              }
              folders {
                total
                needsReview
                sensitive
              }
            }
            reviewStatus
          }
        }
      }
    }
  `
}

export const mapAccessControlDirectory = (raw: any): AccessControlDirectory => {
  const node = raw?.accessControlDirectory?.edges?.[0]?.node

  return {
    id: node?.id ? String(node.id) : null,
    name: node?.name || '',
    path: node?.path || null,
    inherited: node?.inherited || null,
    objectLink: node?.objectLink || null,
    groupAccess: {
      count: node?.groupAccess?.count || 0
    },
    userAccess: {
      count: node?.userAccess?.count || 0
    },
    owner: node?.owner
      ? {
          name: node.owner.name,
          id: String(node.owner.id)
        }
      : null,
    lastModifiedTime: node?.lastModifiedTime || null,
    accessTypes: (node?.accessTypes || []) as AccessFoldersTypes[],
    accessControlDirectoryCounts: {
      files: {
        total: node?.accessControlDirectoryCounts?.files?.total || 0,
        needsReview: node?.accessControlDirectoryCounts?.files?.needsReview || 0,
        sensitive: node?.accessControlDirectoryCounts?.files?.sensitive || 0
      },
      folders: {
        total: node?.accessControlDirectoryCounts?.folders?.total || 0,
        needsReview: node?.accessControlDirectoryCounts?.folders?.needsReview || 0,
        sensitive: node?.accessControlDirectoryCounts?.folders?.sensitive || 0
      }
    },
    reviewStatus: (node?.reviewStatus as AccessFoldersReviewStatuses) || null
  }
}

export const queryPeerFolders = (params: FetchPeerFoldersParams) => {
  const { folderId, datasourceId } = params

  return gql`
    {
      accessControlDirectoryHierarchy(
        datasourceId: "${datasourceId}"
        id: ${folderId}
      ) {
        id
        name
      }
    }
  `
}

export const mapPeerFolders = (raw: any): AccessControlPeerFolders[] => {
  return raw.accessControlDirectoryHierarchy
}

export const queryAccessFoldersGroups = (params: AccessControlDirectoryGroupsAndUsersParams) => {
  const { isFoldersView, folderId, datasourceId, accessModes } = params

  const paramsString = [
    accessModes ? `accessModes: [${FolderAccessModes[accessModes]}]` : ''
  ].filter(Boolean) as string[]

  const groupAccessArgs = paramsString.length > 0 ? `(${paramsString.join(', ')})` : ''

  return gql`
    {
      ${
        isFoldersView
          ? `accessControlDirectory(datasourceId: "${datasourceId}", id: ${folderId})`
          : `accessControlObjects(datasourceIds: ["${datasourceId}"] objectIds: [${folderId}])`
      } {
        edges {
          node {
            groupAccess${groupAccessArgs} {
              count
              edges {
                node {
                  id
                  name
                  type
                  accessModes
                  memberUsers {
                    count
                  }
                  memberGroups {
                    count
                  }
                }
              }
            }
          }
        }
      }
    }
  `
}

export const mapQueryAccessFoldersGroups = (
  raw: any,
  isFoldersView: boolean
): { list: AccessControlDirectoryGroups[]; total: number } => {
  try {
    const groupAccessNodes =
      raw?.[isFoldersView ? 'accessControlDirectory' : 'accessControlObjects']?.edges?.[0]?.node
        ?.groupAccess?.edges || []

    const list =
      groupAccessNodes.map(({ node }) => ({
        id: node?.id || '',
        name: node?.name || '',
        type: node?.type,
        accessModes: node?.accessModes || [],
        usersCount: node?.memberUsers?.count || 0,
        groupsCount: node?.memberGroups?.count || 0
      })) || []

    return {
      list,
      total: raw?.accessControlDirectory?.edges?.[0]?.node?.groupAccess?.count || 0
    }
  } catch (error) {
    console.error(error)
    throw error
  }
}

export const queryAccessFoldersUsers = (params: AccessControlDirectoryGroupsAndUsersParams) => {
  const {
    isFoldersView,
    folderId,
    datasourceId,
    accessModes,
    employeeType,
    departmentName
  } = params

  const paramsString = [
    departmentName ? `departmentName: "${departmentName}"` : '',
    employeeType ? `employeeType: ${UserAccessEmployeeType[employeeType]}` : '',
    accessModes ? `accessModes: [${FolderAccessModes[accessModes]}]` : ''
  ].filter(Boolean) as string[]

  const userAccessArgs = paramsString.length > 0 ? `(${paramsString.join(', ')})` : ''

  return gql`
    {
      ${
        isFoldersView
          ? `accessControlDirectory(datasourceId: "${datasourceId}", id: ${folderId})`
          : `accessControlObjects(datasourceIds: ["${datasourceId}"] objectIds: [${folderId}])`
      } {
        edges {
          node {
            userAccess${userAccessArgs} {
              count
              edges {
                node {
                  id
                  name
                  departmentName
                  employeeType
                  accessModes
                  memberOfGroups {
                    count
                  }
                }
              }
            }
          }
        }
      }
    }
  `
}

export const mapQueryAccessFoldersUsers = (
  raw: any,
  isFoldersView: boolean
): { list: AccessControlDirectoryUsers[]; total: number } => {
  try {
    const userAccessNodes =
      raw?.[isFoldersView ? 'accessControlDirectory' : 'accessControlObjects']?.edges?.[0]?.node
        ?.userAccess?.edges || []

    const list =
      userAccessNodes.map(({ node }) => ({
        id: node?.id || '',
        name: node?.name || '',
        departmentName: node?.departmentName,
        employeeType: node?.employeeType,
        accessModes: node?.accessModes || [],
        groupsCount: node?.memberGroups?.count || 0
      })) || []

    return {
      list,
      total: raw?.accessControlDirectory?.edges?.[0]?.node?.userAccess?.count || 0
    }
  } catch (error) {
    console.error(error)
    throw error
  }
}

export const queryDepartmentFilter = (params: DepartmentFilterParams) => {
  const { datasourceId } = params

  return gql`
    {
      accessControlUsersGroupedByDept(
        first: 50
        after: ""
        datasourceId: "${datasourceId}"
      ) {
        count
        edges {
          node {
            departmentName
          }
        }
      }
    }
  `
}

export const mapQueryDepartmentFilter = (raw: any): { list: DepartmentFilter[] } => {
  try {
    const list =
      raw.accessControlUsersGroupedByDept?.edges?.map(({ node }) => ({
        departmentName: node?.departmentName || ''
      })) || []

    return {
      list
    }
  } catch (error) {
    console.error(error)
    throw error
  }
}

export const queryGetManegarList = (params: DepartmentFilterParams) => {
  const { datasourceId } = params
  const datasourceFilter = datasourceId ? `, datasourceId: "${datasourceId}"` : ''
  return gql`
    {
      accessControlUsers(first: 50, after: "", isManager: true ${datasourceFilter}) {
        edges {
          node {
            id
            name
          }
        }
      }
    }
  `
}

export interface AccessControlUser {
  id: string
  name: string
}

export const mapQueryGetManegarList = (raw: any): { list: AccessControlUser[] } => {
  try {
    const list = raw.accessControlUsers.edges.map(({ node }) => ({
      id: node.id,
      name: node.name
    }))
    return { list }
  } catch (error) {
    console.error(error)
    throw error
  }
}
